import React, { useState, useRef, useEffect } from "react";
import { useMessages } from "../providers/MessagesContext";
import { useAutoScroll } from "../hooks/useAutoScroll";
import { resetSession } from "../utils/apiUtils";
import { ChatContainer } from "../components/chat/ChatContainer";
import { ChatControls } from "../components/chat/ChatControls";
import { useAudio } from "../providers/AudioProvider";
import ConversationModeModal from "../components/ConversationModeModal";
import Header from "../components/Header";
import ChatInput from "../components/chat/ChatInput";
import SpokenMessageItem from "../components/chat/SpokenMessageItem";
import FeedbackModal from "../components/modals/FeedbackModal";
import TermsOfServiceModal from "../components/modals/TermsOfServiceModal";
import ExitConversationMode from "../components/chat/ExitConversationMode";
import { TRANSCRIPTION_CLEAR_TIMER_MS } from "../utils/constants";
import MaintenanceMessage from "../components/MaintenanceMessage";
import { getSystemStatus, SystemStatus } from "../services/SystemStatusService";
import { useAuth } from "../providers/AuthContext";

interface ChatPageProps {
  updateAvailable?: boolean;
  onUpdateAction?: () => void;
  onBackendVersionFetched?: (version: string) => void;
}

const ChatPage: React.FC<ChatPageProps> = ({
  updateAvailable = false,
  onUpdateAction,
  onBackendVersionFetched,
}) => {
  const conversationRef = useRef<HTMLDivElement>(null);
  const { messages } = useMessages();
  const { isInitializing } = useAuth();
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [showTerms, setShowTerms] = useState(true);
  const [modelLoadingProgress, _] = useState("");
  const [dismissedSchedMsg, setDismissedSchedMsg] = useState(false);
  const [maintenanceStatus, setMaintenanceStatus] =
    React.useState<SystemStatus | null>(null);
  const isUnderMaintenance = maintenanceStatus?.is_maintenance || false;
  const {
    conversationMode,
    setConversationMode,
    isModelLoaded: voiceModelLoaded,
  } = useAudio();

  //Auto scroll to the bottom of the messages whenever a new message appears
  useAutoScroll(conversationRef, messages);

  // Reset session when component mounts
  useEffect(() => {
    resetSession();
  }, []);

  // Check maintenance status once authentication is ready
  React.useEffect(() => {
    // Wait for authentication to be ready before checking maintenance
    if (isInitializing) {
      return;
    }

    const checkMaintenance = async () => {
      try {
        const status = await getSystemStatus();
        setMaintenanceStatus(status);

        // Pass backend version up to parent App component
        if (onBackendVersionFetched && status.backend_version) {
          onBackendVersionFetched(status.backend_version);
        }
      } catch (error) {
        console.error("Error checking system status:", error);
        // On error, assume no maintenance
        setMaintenanceStatus({
          is_maintenance: false,
          is_scheduled: false,
          backend_version: "",
        });
      }
    };

    checkMaintenance();
  }, [isInitializing, onBackendVersionFetched]);

  // Prepare scheduled maintenance data for banner
  const scheduledMaintenance =
    maintenanceStatus?.is_scheduled && !maintenanceStatus?.is_maintenance
      ? {
          message: maintenanceStatus.maintenance_message,
          startDateTime: maintenanceStatus.start_at_datetime,
          endDateTime: maintenanceStatus.end_at_datetime,
        }
      : null;

  // Determine if we should show the scheduled maintenance banner
  const shouldShowScheduledMaintenance =
    scheduledMaintenance && !dismissedSchedMsg;

  const handleConversationModeConfirm = async () => {
    setConversationMode(true);
    setShowConversationModal(false);
  };

  return (
    <div
      className={`bg-[#e3f2fd] font-['Poppins',sans-serif] flex flex-col h-[100dvh] w-full`}
    >
      <Header
        updateAvailable={updateAvailable}
        onUpdateAction={onUpdateAction}
        scheduledMaintenance={
          shouldShowScheduledMaintenance ? scheduledMaintenance : null
        }
        onDismissScheduledMaintenance={() => setDismissedSchedMsg(true)}
      />

      <div className="flex-1 flex flex-col items-center p-2 sm:p-3 lg:p-4 xl:p-6 min-h-0 w-full">
        <div className="w-full max-w-3xl flex-1 flex flex-col bg-white/95 rounded-2xl sm:rounded-3xl shadow-lg overflow-hidden relative transition-all duration-500 min-h-0">
          {/* Recording status when conversation mode is enabled and model is loaded */}
          {/* {conversationMode && voiceModelLoaded && <RecordingStatus />} */}

          {/* Exit conversation mode button when conversation mode is enabled */}
          {conversationMode && (
            <ExitConversationMode onExit={() => setConversationMode(false)} />
          )}

          <ChatContainer
            messages={messages}
            conversationRef={conversationRef}
          />

          {/* Live transcription in conversation mode */}
          {voiceModelLoaded && conversationMode && !isUnderMaintenance && (
            <>
              <SpokenMessageItem />
              <div className="p-3 text-xs text-gray-500 text-center">
                Saying "clear message" or waiting{" "}
                {TRANSCRIPTION_CLEAR_TIMER_MS / 1000} seconds will clear the
                message.
              </div>
            </>
          )}

          {/* Input Area - Hide when in conversation mode or maintenance mode */}
          {!conversationMode && !isUnderMaintenance && (
            <>
              <ChatControls />

              <ChatInput onMicClick={() => setShowConversationModal(true)} />
            </>
          )}
        </div>
      </div>

      {showTerms && (
        <TermsOfServiceModal handleTermsAccepted={() => setShowTerms(false)} />
      )}

      <FeedbackModal />

      <ConversationModeModal
        isOpen={showConversationModal}
        onConfirm={handleConversationModeConfirm}
        onCancel={() => setShowConversationModal(false)}
        isModelLoaded={voiceModelLoaded}
        modelLoadingProgress={modelLoadingProgress}
        tutorialDismissed={
          localStorage.getItem("conversationModeTutorialDismissed") === "true"
        }
      />

      {/* Maintenance Overlay - Only for active maintenance */}
      {maintenanceStatus?.is_maintenance && (
        <MaintenanceMessage
          message={maintenanceStatus.maintenance_message}
          endDateTime={maintenanceStatus.end_at_datetime}
        />
      )}
    </div>
  );
};

export default ChatPage;
